import { useEffect } from 'react';
import { useUserData } from 'src/redux/selectors';

import useAppContext from '../../../hooks/useAppContext';

import useLayoutPermissions from './useLayoutPermissions';

import Auth from '@/api/auth';
import useApiQuery from '@/hooks/useApiQuery';
import type { APIHookArg } from '@/types';
import type { Organization } from '@/types/organization';

const useOrgPermissions = (orgId?: string) => {
  const { organization, roles } = useUserData();
  const { selectedOrganization, setSelectedOrganization } = useAppContext();
  const { makeApiCall: getSelectedOrganization } = useApiQuery(
    Auth.fetchV1OrganizationById as unknown as APIHookArg
  );

  const currentOrganization: Organization = selectedOrganization ??
    organization ?? {
      enterpriseName: '',
      id: '',
      productIds: [],
      __v: 0,
      type: '',
    };

  useEffect(() => {
    const fetchOrganization = async (): Promise<void> => {
      const data = (await getSelectedOrganization(orgId)) as Organization;
      setSelectedOrganization?.(data);
    };

    if (orgId && selectedOrganization?._id !== orgId) {
      fetchOrganization();
    }
  }, [setSelectedOrganization, getSelectedOrganization, selectedOrganization, orgId]);

  const isAdminOrg = currentOrganization.enterpriseName === 'admin-org';

  const permissions = useLayoutPermissions(roles ?? [], currentOrganization) as ReturnType<
    typeof useLayoutPermissions
  > | null;

  const showOrganization = (): string | null => {
    if (permissions?.canSeeOwnOrganization && currentOrganization.enterpriseName) {
      return currentOrganization.enterpriseName;
    }
    return selectedOrganization?.enterpriseName ?? null;
  };

  return {
    permissions,
    currentOrganization,
    selectedOrganization,
    showOrganization,
    isAdminOrg,
  };
};

export default useOrgPermissions;
